import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { setPendingImport } from '@/lib/pendingImport'
import { useDocumentDropRouter, DropDecision } from '@/hooks/useDocumentDropRouter'
import { EditProjectSystemModal } from '@/components/projects/EditProjectSystemModal'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import {
  ArrowLeft,
  Upload,
  CheckCircle,
  Clock,
  Trash2,
  RefreshCw,
  Cpu,
  Users,
  Pencil,
  X,
  AlertTriangle,
  Search,
  Mail,
  Phone,
  Package,
  FileText,
  Download,
} from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canDownload, canGenerate, canManage, canUpload } from '@/lib/roles'
import {
  projectsApi,
  ProjectDetail,
  ImportBatch,
  ProjectSystem,
  ProjectContact,
  GlobalContact,
  Shipyard,
  ComplianceRuleProfile,
  AssetInventoryRow,
  RaDocumentRow,
  ProjectDeliverable,
  ProjectRequirements,
  VendorFormMatchResult,
} from '@/lib/projectsApi'
import { VendorRouteModal } from '@/components/modals/VendorRouteModal'
import { contactDisplay } from '@/lib/utils'
import { ImportAssetInventoryModal } from '@/components/modals/ImportAssetInventoryModal'
import { ImportRaWordModal } from '@/components/projects/ImportRaWordModal'
import { WordRaProjectTab } from '@/components/projects/WordRaProjectTab'
import { ScreeningTab } from '@/components/projects/ScreeningTab'
import { AssetInventoryTab } from '@/components/AssetInventoryTab'
import { DirtyFormProvider } from '@/contexts/DirtyFormContext'
import { DirtyChangesBar } from '@/components/DirtyChangesBar'
import { ComplianceProfileSelector } from '@/components/projects/ComplianceProfileSelector'
import { ComplianceWorkspace } from '@/components/projects/ComplianceWorkspace'
import { RaGenerationWorkspace } from '@/components/projects/RaGenerationWorkspace'
import { ZoneConduitWorkspace } from '@/components/projects/ZoneConduitWorkspace'
import { CsddWorkspace } from '@/components/projects/CsddWorkspace'
import { TestProcedureWorkspace } from '@/components/projects/TestProcedureWorkspace'
import { NmsProjectTab } from '@/components/projects/NmsProjectTab'
import { useTranslation } from 'react-i18next'

function taBucket(status: string | null): 'approved' | 'pending' | 'other' {
  if (!status) return 'other'
  const s = status.toLowerCase().trim()
  if (s === 'o' || s.includes('approved') || s.includes('yes') || s === 'ta') return 'approved'
  if (s === 'x' || s.includes('pending') || s.includes('progress') || s.includes('no')) return 'pending'
  return 'other'
}

function taBadge(status: string | null) {
  const bucket = taBucket(status)
  const cls = {
    approved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    pending:  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    other:    'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
  }[bucket]
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase ${cls}`}>
      {status ?? '—'}
    </span>
  )
}

function cbsGradeBadge(grade: string | null | undefined) {
  if (!grade) return null
  const styles: Record<string, string> = {
    A: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    B: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    C: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  }
  const cls = styles[grade] ?? 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300'
  return (
    <span
      title={`CBS Grade ${grade}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}
    >
      {grade}
    </span>
  )
}

// per-system marks in the old presence-chip style, driven by the NEW
// requirements logic (display only — the model is unchanged):
//   RA — the RA requirement (any source). THREE states: done = filled
//        emerald, partial (thin Word RA only) = amber, missing = hollow.
//   W  — INFORMATIONAL: a mapped Word RA document exists for this system.
//        NOT the requirement (that's the RA mark), just source presence.
//   A  — the inventory requirement (any source): done = filled, missing
//        = hollow. same look as the old Asset mark.
function markChip(letter: string, tone: 'filled' | 'amber' | 'hollow', fill: string, title: string) {
  const cls =
    tone === 'filled' ? `${fill} text-white`
    : tone === 'amber'  ? 'bg-amber-500 text-white'
    : 'border border-slate-300 dark:border-slate-600 text-slate-300 dark:text-slate-600'
  return (
    <span
      title={title}
      className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-0.5 rounded text-[9px] font-bold leading-none ${cls}`}
    >
      {letter}
    </span>
  )
}

function requirementMarks(s: ProjectSystem) {
  const ra = s.ra_status ?? 'missing'
  // which source satisfied it: done with no Excel source can only be a
  // complete Word RA; done with Excel is covered by Excel regardless of
  // the Word RA's completeness
  const raTitle =
    ra === 'done'
      ? s.has_excel_ra
        ? (s.has_word_ra ? 'RA data: done (Excel + Word RA present)' : 'RA data: done via Excel RA')
        : 'RA data: done via Word RA'
      : ra === 'partial'
        ? 'RA data: partial — Word RA incomplete, no other source'
        : 'RA data: missing'
  const invDone = (s.inventory_status ?? 'missing') === 'done'
  return (
    <span className="inline-flex items-center gap-1" aria-label="Requirement status">
      {markChip('RA', ra === 'done' ? 'filled' : ra === 'partial' ? 'amber' : 'hollow', 'bg-emerald-500', raTitle)}
      {markChip('W', s.has_word_ra ? 'filled' : 'hollow', 'bg-indigo-500',
        `Word RA document: ${s.has_word_ra ? 'present' : 'not present'}`)}
      {markChip('A', invDone ? 'filled' : 'hollow', 'bg-sky-500',
        `Asset inventory: ${invDone ? 'done' : 'missing'}`)}
    </span>
  )
}

// Item A: sentinel group key for out-of-scope systems. the backend already
// orders them last, so this key's Map entry lands at the end. the render
// special-cases it into a single "Out of scope" group header
const OUT_OF_SCOPE_GROUP = '__out_of_scope__'

// small amber marker for a system whose scope verdict is 'uncertain' (AI
// wasn't sure, needs review). distinct from NULL/unprocessed, which shows
// nothing
function scopeUncertainMark(s: ProjectSystem) {
  if (s.scope_status !== 'uncertain') return null
  return (
    <span
      title="Scope uncertain — AI wasn't sure, needs review"
      className="inline-flex items-center justify-center h-4 px-1 rounded text-[9px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
    >
      ?
    </span>
  )
}

// requirement-based row filter, matching the RA / INV marks the row shows.
// 'complete' = both requirements met (NOT "all upload types present" — a
// Word RA alone satisfies the RA requirement). ra_completion_status stays
// in the API response (other consumers) but the FE keys off requirements
type PresenceFilter = 'all' | 'ra_missing' | 'ra_partial' | 'inv_missing' | 'complete'

function matchesPresenceFilter(s: ProjectSystem, f: PresenceFilter): boolean {
  switch (f) {
    case 'ra_missing':  return (s.ra_status ?? 'missing') === 'missing'
    case 'ra_partial':  return s.ra_status === 'partial'
    case 'inv_missing': return (s.inventory_status ?? 'missing') === 'missing'
    case 'complete':    return Boolean(s.requirements_met)
    default:            return true   // 'all'
  }
}

function batchStatusDot(b: ImportBatch) {
  if (b.status === 'committed') return <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
  if (b.status === 'draft')     return <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
  return <span className="h-2 w-2 rounded-full bg-slate-300 shrink-0" />
}

export const ProjectDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const currentProjectIdRef = useRef(id)
  currentProjectIdRef.current = id
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { success, error: showError } = useToast()
  const { user } = useAuth()

  const canEdit          = canManage(user)
  const canUploadFiles   = canUpload(user)
  const canDownloadFiles = canDownload(user)
  const canGenerateFiles = canGenerate(user)
  const canDelete        = canManage(user)
  const canEditInventory = canManage(user)

  type ProjectTab = 'compliance' | 'overview' | 'word-ra' | 'documents' | 'wiring' | 'nms'
  type DocumentSubtab = 'risk-assessment' | 'asset-inventory' | 'zones' | 'csdd' | 'test-procedure'
  const tabParam = searchParams.get('tab')
  const documentParam = searchParams.get('document')
  const hasSystemDeepLink = Boolean(searchParams.get('sys'))
  const activeTab: ProjectTab =
    tabParam === 'overview'  ? 'overview'  :
    tabParam === 'word-ra'   ? 'word-ra'   :
    tabParam === 'nms'       ? 'nms'       :
    // compatibility: the first release exposed these as top-level tabs.
    (tabParam === 'zones' || tabParam === 'csdd' || tabParam === 'testing') ? 'documents' :
    // legacy asset-inventory links now land on Documents > Asset Inventory.
    tabParam === 'inventory' ? 'documents' :
    tabParam === 'documents' ? 'documents' :
    // Wiring diagrams are available to project managers (strategy/super_admin).
    // 'screening' stays as an alias for old links.
    (tabParam === 'wiring' || tabParam === 'screening') && canEdit ? 'wiring' :
    hasSystemDeepLink ? 'overview' :
                        'compliance'
  const activeDocumentSubtab: DocumentSubtab =
    tabParam === 'zones' || documentParam === 'zones' ? 'zones'
    : tabParam === 'csdd' || documentParam === 'csdd' ? 'csdd'
    : tabParam === 'testing' || documentParam === 'test-procedure' ? 'test-procedure'
    : tabParam === 'inventory' || documentParam === 'asset-inventory' ? 'asset-inventory'
    : 'risk-assessment'
  const setActiveTab = (tab: ProjectTab) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'compliance') {
      next.delete('tab')
      next.delete('sys')
    }
    else next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }
  const setDocumentSubtab = (subtab: DocumentSubtab) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'documents')
    if (subtab === 'risk-assessment') next.delete('document')
    else next.set('document', subtab)
    setSearchParams(next, { replace: true })
  }

  // Phase 3.6.1: page-level drop router. project-detail had NO drop handler
  // pre-fix (Sam's "current total breakage", drops just navigated the
  // browser to the file). now:
  //   .xlsx → setPendingImport + navigate to /projects/:id/import
  //           (matches the existing Upload button + the ProjectsPage
  //           list-page drop flow)
  //   anything else → toast "Open a system page to attach evidence"
  //                   (evidence is per-system, not per-project)
  const dropDecision = useCallback((f: { name: string; type: string }): DropDecision => {
    const isXls = /\.xlsx?$/i.test(f.name)
                || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                || f.type === 'application/vnd.ms-excel'
    // wiring drawing PDFs route to the Yard Wiring Diagram tab (the
    // document-level router wins every drop by design, so the tab's
    // own drop zone never sees one — this is its path in). 'parse' is
    // the unused batch-capable action slot on this page.
    const isPdf = /\.pdf$/i.test(f.name) || f.type === 'application/pdf'
    if (isPdf && f.name) {
      return {
        action: 'parse',
        overlayMessage: 'Drop wiring drawing PDFs — added to Yard Wiring Diagram for screening',
      }
    }
    if (isXls || !f.name) {
      return {
        action: 'import',
        overlayMessage: f.type === 'application/pdf'
          ? 'Drop wiring drawing PDFs — added to Yard Wiring Diagram for screening'
          : 'Drop .xlsx to import systems into this project',
      }
    }
    return {
      action: 'reject',
      overlayMessage: 'Drop .xlsx to import systems into this project',
      rejectReason:   'evidence attaches to a specific system. Open a system page first. (Wiring drawing PDFs go to the Yard Wiring Diagram tab.)',
    }
  }, [])

  // PDFs dropped anywhere on the project page land in the wiring tab's
  // upload panel (prop-passed so a not-yet-mounted tab can't miss them)
  const [wiringDropFiles, setWiringDropFiles] = useState<File[] | null>(null)

  // Yard Wiring Diagram tab badge: a running screening job should be
  // visible from ANY tab. Polls the (light) run endpoint every 5s ONLY
  // while a run is processing; goes quiet otherwise and is woken by
  // the tab's run-started event. Not a job tray — just a badge.
  const [screeningProgress, setScreeningProgress] = useState<{ done: number; total: number } | null>(null)
  const screeningPollRunRef = useRef<string | null>(null)
  useEffect(() => {
    if (!id || !canEdit) return
    let timer: number | null = null
    let stopped = false
    const tick = async () => {
      try {
        if (!screeningPollRunRef.current) {
          const runs = await projectsApi.listScreeningRuns(id)
          if (stopped) return
          const proc = runs.find(r => r.status === 'processing')
          if (!proc) { setScreeningProgress(null); return }  // quiet until woken
          screeningPollRunRef.current = proc.id
        }
        const run = await projectsApi.getScreeningRun(screeningPollRunRef.current)
        if (stopped) return
        if (run.status === 'processing') {
          setScreeningProgress({ done: run.systems_done, total: run.systems_total })
          timer = window.setTimeout(tick, 5000)
        } else {
          screeningPollRunRef.current = null
          setScreeningProgress(null)
        }
      } catch {
        if (!stopped) timer = window.setTimeout(tick, 10000)
      }
    }
    tick()
    const onStarted = (e: Event) => {
      screeningPollRunRef.current = (e as CustomEvent).detail?.runId ?? null
      if (timer) window.clearTimeout(timer)
      void tick()
    }
    window.addEventListener('seacuredb:screening-run-started', onStarted)
    return () => {
      stopped = true
      if (timer) window.clearTimeout(timer)
      window.removeEventListener('seacuredb:screening-run-started', onStarted)
    }
  }, [id, canEdit])

  const { dragging: pageDragging, overlayMessage: pageOverlayMessage } = useDocumentDropRouter({
    classify: dropDecision,
    onParse: (files) => {
      if (!canUploadFiles) { showError(t('projectDetail.screeningAdminRequired')); return }
      setWiringDropFiles(files)
      setActiveTab('wiring')
    },
    onImport: async (file) => {
      if (!canUploadFiles) { showError(t('projectDetail.importAdminRequired')); return }
      // a vendor-response form goes to the match-and-attach flow. anything
      // else (system register, etc.) falls through to the normal import
      setVendorRouting(true)
      try {
        const result = await projectsApi.vendorFormParseAndMatch(id!, file)
        if (result.is_vendor_form) { setVendorRoute({ file, result }); return }
      } catch (e: any) {
        console.warn('[vendor-form route] fell through to import:', e?.message)
      } finally {
        setVendorRouting(false)
      }
      setPendingImport(file)
      navigate(`/projects/${id}/import`)
    },
    onReject: (msg) => showError(msg),
    enabled: !!canUploadFiles,
  })

  const [project,  setProject]  = useState<ProjectDetail | null>(null)
  const [loading,  setLoading]  = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)

  const [showEditModal,    setShowEditModal]    = useState(false)
  // QA item 2: per-row system edit. editSystem drives the modal. ctxMenu
  // drives the right-click context menu (single Edit entry, closed on
  // click-away / Escape via the overlay)
  const [editSystem, setEditSystem] = useState<ProjectSystem | null>(null)
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; system: ProjectSystem } | null>(null)
  const [editSubmitting,   setEditSubmitting]   = useState(false)
  const [editShipyards,    setEditShipyards]    = useState<Shipyard[]>([])
  const [availableComplianceProfiles, setAvailableComplianceProfiles] = useState<ComplianceRuleProfile[]>([])
  const [editHull,         setEditHull]         = useState('')
  const [editShipyardId,   setEditShipyardId]   = useState<number | ''>('')
  const [editVesselName,   setEditVesselName]   = useState('')
  const [editImo,          setEditImo]          = useState('')
  const [editMmsi,         setEditMmsi]         = useState('')
  const [editDeliveredAt,  setEditDeliveredAt]  = useState('')
  const [editVesselType,   setEditVesselType]   = useState('')
  const [editShipOwner,    setEditShipOwner]    = useState('')
  const [editComplianceProfileCodes, setEditComplianceProfileCodes] = useState<string[]>([])

  const [showDeleteModal,  setShowDeleteModal]  = useState(false)
  const [deleteConfirm,    setDeleteConfirm]    = useState('')
  const [deletingProject,  setDeletingProject]  = useState(false)

  const [systems,  setSystems]  = useState<ProjectSystem[]>([])
  // vendor-form auto-route (drop a vendor-response form on the overview)
  const [vendorRoute, setVendorRoute] = useState<{ file: File; result: VendorFormMatchResult } | null>(null)
  const [vendorRouting, setVendorRouting] = useState(false)
  const [contacts, setContacts] = useState<ProjectContact[]>([])
  const [globalContacts, setGlobalContacts] = useState<GlobalContact[]>([])
  // ?sys= deep-link (smart-search click-through): land with the systems list
  // pre-filtered to the located system
  const [systemFilter, setSystemFilter] = useState(() => searchParams.get('sys') ?? '')
  const [statusFilter, setStatusFilter] = useState<PresenceFilter>('all')
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false)
  const [inventoryCount, setInventoryCount] = useState<number | null>(null)
  const [inventoryRows, setInventoryRows] = useState<AssetInventoryRow[]>([])

  const [raDocuments, setRaDocuments] = useState<RaDocumentRow[]>([])
  const [raDocsLoading, setRaDocsLoading] = useState(false)
  const [raImportOpen, setRaImportOpen] = useState(false)

  // Documents tab: presence-gated deliverable availability + the project
  // requirements checklist (same server-side predicates as the dashboard)
  const [deliverables, setDeliverables] = useState<ProjectDeliverable[] | null>(null)
  const [requirements, setRequirements] = useState<ProjectRequirements | null>(null)
  const [deliverablesLoading, setDeliverablesLoading] = useState(false)
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null)
  const projectLoadSequence = useRef(0)
  const raDocsLoadSequence = useRef(0)
  const deliverablesLoadSequence = useRef(0)
  const loadDeliverables = useCallback(async () => {
    if (!id) {
      deliverablesLoadSequence.current++
      setDeliverables(null)
      setRequirements(null)
      setDeliverablesLoading(false)
      return
    }
    if (currentProjectIdRef.current !== id) return
    const sequence = ++deliverablesLoadSequence.current
    setDeliverablesLoading(true)
    try {
      const { deliverables, requirements } = await projectsApi.getDeliverables(id)
      if (sequence !== deliverablesLoadSequence.current || currentProjectIdRef.current !== id) return
      setDeliverables(deliverables)
      setRequirements(requirements ?? null)
    } catch (err: any) {
      if (sequence !== deliverablesLoadSequence.current || currentProjectIdRef.current !== id) return
      showError(err?.message ?? t('projectDetail.loadDeliverablesFailed'))
    } finally {
      if (sequence === deliverablesLoadSequence.current && currentProjectIdRef.current === id) setDeliverablesLoading(false)
    }
  }, [id, showError, t])

  // download a deliverable. each generator fail-louds itself; we only offer
  // the download when availability says it will work.
  const handleDeliverableDownload = async (d: ProjectDeliverable) => {
    if (!d.available || !id) return
    setDownloadingKey(d.key)
    try {
      if (d.key === 'ra' && d.ra_document_id) {
        await projectsApi.downloadGeneratedRa(d.ra_document_id, `RA_${project?.hull_number ?? 'vessel'}.docx`)
        success(t('projectDetail.raDownloaded'))
      } else if (d.key === 'asset_inventory') {
        await projectsApi.generateAssetInventoryDeliverable(
          id,
          `${project?.hull_number ?? 'vessel'}_Target_Systems_Asset_Inventory.xlsx`,
          true,
        )
        success(t('projectDetail.inventoryDownloaded'))
      } else if (d.key === 'zcd' || d.key === 'csdd') {
        await projectsApi.downloadComplianceDocument(
          id,
          d.key,
          `${project?.hull_number ?? 'vessel'}_${d.key.toUpperCase()}_Rev.0.docx`,
        )
        success(t('projectDetail.documentDownloaded'))
      }
    } catch (e: any) {
      showError(e?.message ?? t('projectDetail.generateDocumentFailed'))
      loadDeliverables()  // state may have drifted (e.g. systems edited)
    } finally {
      setDownloadingKey(null)
    }
  }

  const loadProject = useCallback(async () => {
    if (!id) {
      projectLoadSequence.current++
      setProject(null)
      setSystems([])
      setContacts([])
      setGlobalContacts([])
      setInventoryRows([])
      setInventoryCount(null)
      setLoading(false)
      return
    }
    if (currentProjectIdRef.current !== id) return
    const sequence = ++projectLoadSequence.current
    setLoading(true)
    try {
      const [p, sysResult, contactsResult, globalContactsResult, inventoryResult] = await Promise.all([
        projectsApi.getProject(id),
        projectsApi.listProjectSystems(id).then(
          value => ({ value, error: null }),
          error => ({ value: [] as ProjectSystem[], error }),
        ),
        projectsApi.listProjectContacts(id).then(
          value => ({ value, error: null }),
          error => ({ value: [] as ProjectContact[], error }),
        ),
        projectsApi.listContacts().then(
          value => ({ value, error: null }),
          error => ({ value: [] as GlobalContact[], error }),
        ),
        projectsApi.getAssetInventory(id).then(
          value => ({ value, error: null }),
          error => ({ value: [] as AssetInventoryRow[], error }),
        ),
      ])
      if (sequence !== projectLoadSequence.current || currentProjectIdRef.current !== id) return
      setProject(p)
      setSystems(sysResult.value)
      setContacts(contactsResult.value)
      setGlobalContacts(globalContactsResult.value)
      setInventoryRows(inventoryResult.value)
      setInventoryCount(inventoryResult.value.length)
      const failedSections = [
        sysResult.error && 'systems',
        contactsResult.error && 'project contacts',
        globalContactsResult.error && 'contact links',
        inventoryResult.error && 'asset inventory',
      ].filter(Boolean)
      if (failedSections.length > 0) {
        showError(`Failed to load ${failedSections.join(', ')}`, 'The project data was not deleted. Refresh after the server issue is resolved.')
      }
    } catch (err: any) {
      if (sequence !== projectLoadSequence.current || currentProjectIdRef.current !== id) return
      showError(err.message ?? t('projectDetail.loadFailed'))
    } finally {
      if (sequence === projectLoadSequence.current && currentProjectIdRef.current === id) setLoading(false)
    }
  }, [id, showError, t])

  useEffect(() => {
    // Remove the previous project's route-bound state before starting the
    // next request. Sequence guards prevent late responses from restoring it.
    projectLoadSequence.current++
    raDocsLoadSequence.current++
    deliverablesLoadSequence.current++
    setProject(null)
    setSystems([])
    setContacts([])
    setGlobalContacts([])
    setInventoryRows([])
    setInventoryCount(null)
    setRaDocuments([])
    setDeliverables(null)
    setRequirements(null)
    setShowEditModal(false)
    setEditSystem(null)
    setCtxMenu(null)
    setShowDeleteModal(false)
    setDeleteConfirm('')
    setInventoryModalOpen(false)
    setRaImportOpen(false)
    setDeleting(null)
    setDeletingProject(false)
    setDownloadingKey(null)
    void loadProject()
  }, [id, loadProject])

  // load deliverable availability when the Documents tab is opened
  useEffect(() => {
    if (activeTab === 'documents' && id) loadDeliverables()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id])

  const loadRaDocuments = useCallback(async () => {
    if (!id) {
      raDocsLoadSequence.current++
      setRaDocuments([])
      setRaDocsLoading(false)
      return
    }
    if (currentProjectIdRef.current !== id) return
    const sequence = ++raDocsLoadSequence.current
    setRaDocsLoading(true)
    try {
      const docs = await projectsApi.listRaDocuments(id)
      if (sequence !== raDocsLoadSequence.current || currentProjectIdRef.current !== id) return
      setRaDocuments(docs)
    } catch (err: any) {
      // surfaced by api layer, keep list as-is
    } finally {
      if (sequence === raDocsLoadSequence.current && currentProjectIdRef.current === id) setRaDocsLoading(false)
    }
  }, [id])

  // eager-load Word RA documents for the Overview badge + Manage drawer
  useEffect(() => {
    void loadRaDocuments()
  }, [loadRaDocuments])

  const systemContactsMap = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const c of globalContacts) {
      const display = c.full_name || c.email || 'Unknown'
      for (const ls of c.linked_systems ?? []) {
        if (ls.project_id !== id) continue
        const arr = map.get(ls.system_id) ?? []
        arr.push(display)
        map.set(ls.system_id, arr)
      }
    }

    console.log('[ProjectDetailPage] systemContactsMap', {
      size: map.size,
      projectId: id,
      sampleContact: globalContacts[0]
        ? { name: globalContacts[0].full_name, linked_systems: globalContacts[0].linked_systems }
        : null,
      sampleEntries: [...map.entries()].slice(0, 3),
    })
    return map
  }, [globalContacts, id])

  const systemsByFunction = useMemo(() => {
    const filter = systemFilter.trim().toLowerCase()
    let filtered = filter
      ? systems.filter(s =>
          (s.function_name ?? '').toLowerCase().includes(filter) ||
          (s.system_description ?? '').toLowerCase().includes(filter) ||
          (s.system_name ?? '').toLowerCase().includes(filter) ||
          (s.vendor_name_raw ?? '').toLowerCase().includes(filter)
        )
      : systems
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => matchesPresenceFilter(s, statusFilter))
    }
    // F2: group under the regulatory function (sort_function = Word RA
    // function when mapped, else Excel), and PRESERVE the backend order.
    // the list arrives pre-sorted by regulation function order with
    // unmapped/no-Word-RA systems after the aligned ones. Map keeps
    // insertion order, so function groups render in that order. a function
    // that's entirely unmapped (e.g. "Other") first appears in the trailing
    // unmapped block and so lands at the end.
    const map = new Map<string, ProjectSystem[]>()
    for (const s of filtered) {
      // out-of-scope systems collapse into one trailing group (backend
      // sorts them last so this entry lands at the end). others group by
      // canonical function header (collapses long/short name variants at
      // one regulatory position), raw function name otherwise.
      const key = s.scope_status === 'out_of_scope'
        ? OUT_OF_SCOPE_GROUP
        : (s.sort_display ?? s.sort_function ?? s.function_name)?.trim() || 'Unassigned'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(s)
    }
    return [...map.entries()]
  }, [systems, systemFilter, statusFilter])

  const taSummary = useMemo(() => {
    let approved = 0, pending = 0, other = 0
    for (const s of systems) {
      const b = taBucket(s.type_approval_status)
      if (b === 'approved') approved++
      else if (b === 'pending') pending++
      else other++
    }
    return { approved, pending, other }
  }, [systems])

  const openEditModal = () => {
    if (!project) return
    setEditHull(project.hull_number)
    setEditShipyardId(project.shipyard_id ?? '')
    setEditVesselName(project.vessel_name ?? '')
    setEditImo(project.imo_number ?? '')
    setEditMmsi(project.mmsi ?? '')
    setEditDeliveredAt(project.delivered_at?.slice(0, 10) ?? '')
    setEditVesselType(project.vessel_type ?? '')
    setEditShipOwner(project.ship_owner ?? '')
    setEditComplianceProfileCodes(
      (project.compliance_profiles ?? [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(profile => profile.profile_code),
    )
    if (editShipyards.length === 0) {
      projectsApi.listShipyards().then(setEditShipyards).catch(() => {})
    }
    if (availableComplianceProfiles.length === 0) {
      projectsApi.listComplianceProfiles()
        .then(setAvailableComplianceProfiles)
        .catch(err => showError(err?.message ?? t('projectDetail.loadProfilesFailed')))
    }
    setShowEditModal(true)
  }

  const handleEditSubmit = async () => {
    if (!id || !editHull.trim()) return
    if (editMmsi.trim() && !/^[0-9]{9}$/.test(editMmsi.trim())) {
      showError('MMSI must contain exactly 9 digits')
      return
    }
    if (editDeliveredAt && !editMmsi.trim()) {
      showError('Enter a verified MMSI before marking this vessel delivered')
      return
    }
    if (editComplianceProfileCodes.length < 1 || editComplianceProfileCodes.length > 2) {
      showError(t('projectCreate.selectProfiles'))
      return
    }
    setEditSubmitting(true)
    try {
      await projectsApi.updateProject(id, {
        hull_number:  editHull.trim(),
        shipyard_id:  editShipyardId !== '' ? editShipyardId : null,
        vessel_name:  editVesselName.trim()  || null,
        imo_number:   editImo.trim()         || null,
        mmsi:         editMmsi.trim()        || null,
        delivered_at: editDeliveredAt        || null,
        vessel_type:  editVesselType.trim()  || null,
        ship_owner:   editShipOwner.trim()   || null,
        compliance_profiles: editComplianceProfileCodes.map(profile_code => ({ profile_code })),
      })
      success(t('projectDetail.updated'))
      setShowEditModal(false)
      await loadProject()
    } catch (err: any) {
      showError(err.message ?? t('projectDetail.updateFailed'))
    } finally {
      setEditSubmitting(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!id || !project) return
    setDeletingProject(true)
    try {
      await projectsApi.deleteProject(id)
      success(t('projectDetail.deleted', { hull: project.hull_number }))
      navigate('/dashboard')
    } catch (err: any) {
      showError(err.message ?? t('projectDetail.deleteFailed'))
      setDeletingProject(false)
    }
  }

  const handleDeleteBatch = async (batchId: string) => {
    if (!confirm(t('projectDetail.confirmDeleteDraft'))) return
    setDeleting(batchId)
    try {
      await projectsApi.deleteBatch(batchId)
      success(t('projectDetail.draftDeleted'))
      await loadProject()
    } catch (err: any) {
      showError(err.message ?? t('projectDetail.deleteDraftFailed'))
    } finally {
      setDeleting(null)
    }
  }

  const breadcrumbs = project
    ? [
        { label: t('nav.dashboard'), to: '/dashboard' },
        { label: project.shipyard_name ?? t('projectDetail.unassigned') },
        { label: project.hull_number },
      ]
    : [
        { label: t('nav.dashboard'), to: '/dashboard' },
        { label: '…' },
      ]

  if (loading || (project !== null && project.id !== id)) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <p className="text-slate-500 dark:text-slate-400">{t('common.loading')}</p>
      </div>
    )
  }
  if (!project) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <p className="text-slate-500 dark:text-slate-400">{t('projectDetail.notFound')}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white relative">

      {/* vendor-form auto-route: reading overlay + match/attach modal */}
      {vendorRouting && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-lg px-4 py-3 text-sm shadow">{t('projectDetail.readingVendorForm')}</div>
        </div>
      )}
      {vendorRoute && (
        <VendorRouteModal
          projectId={id!}
          file={vendorRoute.file}
          result={vendorRoute.result}
          onClose={() => setVendorRoute(null)}
          onSuccess={() => { setVendorRoute(null); loadProject() }}
        />
      )}

      {/* Drag overlay (page-level drop router from useDocumentDropRouter) */}
      {pageDragging && (
        <div className="fixed inset-0 z-40 bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl px-10 py-8 text-center border-2 border-dashed border-blue-400">
            <Upload className="h-8 w-8 text-blue-500 mx-auto mb-3" />
            <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {pageOverlayMessage || t('projectDetail.dropFile')}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {t('projectDetail.evidenceAttachHint')}
            </div>
          </div>
        </div>
      )}

      {/* QA item 2: per-row context menu (single Edit entry). the
          full-screen transparent overlay closes it on any click-away.
          fixed-position menu at the cursor */}
      {canEdit && ctxMenu && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setCtxMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null) }}
        >
          <div
            className="absolute bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md shadow-lg py-1 min-w-[140px]"
            style={{
              left: Math.min(ctxMenu.x, window.innerWidth - 160),
              top:  Math.min(ctxMenu.y, window.innerHeight - 60),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setEditSystem(ctxMenu.system); setCtxMenu(null) }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Pencil className="h-3.5 w-3.5 text-slate-400" />
              {t('common.edit')}
            </button>
          </div>
        </div>
      )}

      {/* QA item 2: system edit modal */}
      {canEdit && editSystem && (
        <EditProjectSystemModal
          system={editSystem}
          onClose={() => setEditSystem(null)}
          onSaved={() => {
            setEditSystem(null)
            loadProject()
          }}
        />
      )}

      {/* Edit modal */}
      {canEdit && showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="font-semibold">{t('projectDetail.editProject')}</h2>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('reports.hullNumber')} <span className="text-red-500">*</span>
                </label>
                <input
                  value={editHull}
                  onChange={e => setEditHull(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('reports.shipyard')}</label>
                <select
                  value={editShipyardId}
                  onChange={e => setEditShipyardId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                >
                  <option value="">{t('projectCreate.selectShipyard')}</option>
                  {editShipyards.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('reports.vesselName')}</label>
                <input
                  value={editVesselName}
                  onChange={e => setEditVesselName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('projectCreate.imoNumber')}</label>
                <input
                  value={editImo}
                  onChange={e => setEditImo(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 dark:border-blue-900 dark:bg-blue-950/25">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    MMSI
                  </label>
                  <input
                    value={editMmsi}
                    onChange={e => setEditMmsi(e.target.value.replace(/\D/g, '').slice(0, 9))}
                    inputMode="numeric"
                    maxLength={9}
                    placeholder="9-digit AIS identity"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Delivery date
                  </label>
                  <input
                    type="date"
                    value={editDeliveredAt}
                    onChange={e => setEditDeliveredAt(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                  />
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                    A verified MMSI and delivery date add this vessel to Delivered Fleet.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('projectCreate.vesselType')}</label>
                <input
                  value={editVesselType}
                  onChange={e => setEditVesselType(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('reports.shipOwner')}</label>
                <input
                  value={editShipOwner}
                  onChange={e => setEditShipOwner(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                />
              </div>
              {availableComplianceProfiles.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('projectDetail.classificationProfiles')} <span className="text-red-500">*</span>
                  </label>
                  <ComplianceProfileSelector
                    profiles={availableComplianceProfiles}
                    selectedCodes={editComplianceProfileCodes}
                    onChange={setEditComplianceProfileCodes}
                    disabled={editSubmitting}
                  />
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <button onClick={() => setShowEditModal(false)} className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                {t('common.cancel')}
              </button>
              <Button
                onClick={handleEditSubmit}
                disabled={!editHull.trim() || editComplianceProfileCodes.length === 0 || editSubmitting}
              >
                {editSubmitting ? t('forms.savingChanges') : t('projectDetail.saveChanges')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
              <h2 className="font-semibold text-red-600 dark:text-red-400">{t('projectDetail.deleteProject')}</h2>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {t('projectDetail.deleteWarning', { hull: project.hull_number })}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('projectDetail.typeToConfirm', { hull: project.hull_number })}
              </p>
              <input
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder={project.hull_number}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm font-mono"
              />
            </div>
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <button onClick={() => setShowDeleteModal(false)} className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                {t('common.cancel')}
              </button>
              <Button
                onClick={handleDeleteProject}
                disabled={deleteConfirm !== project.hull_number || deletingProject}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {deletingProject ? t('projectDetail.deleting') : t('projectDetail.deleteProject')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <Breadcrumb items={breadcrumbs} />
            <h1 className="text-lg sm:text-xl font-semibold mt-0.5">
              {project.vessel_name ?? project.hull_number}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadProject} title={t('common.refresh')}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={openEditModal} title={t('projectDetail.editProject')}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setDeleteConfirm(''); setShowDeleteModal(true) }}
                title={t('projectDetail.deleteProject')}
                className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {/* Match the import modal's upload gate so read-only users never
                see a button that opens a modal they cannot use. */}
            {canUploadFiles && (
              <Button variant="outline" onClick={() => setInventoryModalOpen(true)}>
                <Package className="h-4 w-4 mr-2" />
                {t('projectDetail.importInventory')}
              </Button>
            )}
            {canUploadFiles && (
              <Button onClick={() => navigate(`/projects/${id}/import`)}>
                <Upload className="h-4 w-4 mr-2" />
                {t('projectDetail.importWorkbook')}
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" onClick={() => setActiveTab('documents')} title={t('projectDetail.documentsTitle')}>
                <FileText className="h-4 w-4 mr-2" />
                {t('projectDetail.tabs.documents')}
              </Button>
            )}
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-4 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          <StatItem label={t('reports.shipyard')} value={project.shipyard_name ?? '—'} />
          <StatItem label={t('projectDetail.hull')} value={project.hull_number} mono />
          <StatItem label="IMO"      value={project.imo_number ?? '—'} mono />
          {project.mmsi && <StatItem label="MMSI" value={project.mmsi} mono />}
          {project.delivered_at && <StatItem label="Delivered" value={project.delivered_at.slice(0, 10)} />}
          <StatItem label={t('projectDetail.vessel')} value={project.vessel_type ?? '—'} />
          <StatItem label={t('projectDetail.owner')} value={project.ship_owner ?? '—'} />
          <StatItem label={t('nav.systems')} value={String(systems.length)} />
          <StatItem label={t('nav.contacts')} value={String(contacts.length)} />
          <StatItem label={t('projectDetail.inventory')} value={inventoryCount !== null ? String(inventoryCount) : '—'} />
          {(project.compliance_profiles ?? []).length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 uppercase tracking-wide text-[10px]">{t('projectDetail.class')}</span>
              <div className="flex flex-wrap gap-1">
                {project.compliance_profiles.map(profile => (
                  <span
                    key={profile.profile_code}
                    title={`${profile.display_name} · ${profile.edition_label} · ${profile.notation_qualifier || profile.notation_label}`}
                    className="text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium"
                  >
                    {profile.class_society}
                    {profile.position === 2 ? ' · 2' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex gap-6 -mb-px">
          {[
            { key: 'compliance' as const, label: t('projectDetail.tabs.compliance') },
            { key: 'overview'  as const, label: t('projectDetail.tabs.overview') },
            // drawings + screening is admin-only (every endpoint requireAdmin)
            ...(canEdit ? [{ key: 'wiring' as const, label: t('projectDetail.tabs.wiring') }] : []),
            { key: 'word-ra'   as const, label: 'Word RA' },
            { key: 'nms'       as const, label: 'NMS' },
            { key: 'documents' as const, label: t('projectDetail.tabs.documents') },
          ].map(t => {
            const active = activeTab === t.key
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={
                  'pb-2.5 pt-3 text-sm font-medium border-b-2 transition-colors ' +
                  (active
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200')
                }
              >
                {t.label}
                {t.key === 'word-ra' && raDocuments.length > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-slate-400">({raDocuments.length})</span>
                )}
                {t.key === 'wiring' && screeningProgress && (
                  <span className="ml-1.5 text-xs font-medium text-blue-500 animate-pulse tabular-nums">
                    {screeningProgress.total > 0
                      ? `${screeningProgress.done}/${screeningProgress.total}`
                      : '●'}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto">
        {activeTab === 'compliance' && (
          <ComplianceWorkspace
            project={project}
            systemCount={systems.length}
            canEdit={canEdit}
            onEditSetup={openEditModal}
            onOpenWiring={() => setActiveTab('wiring')}
            onOpenDocuments={(subtab = 'risk-assessment') => setDocumentSubtab(subtab)}
            onOpenZones={() => setDocumentSubtab('zones')}
            onOpenCsdd={() => setDocumentSubtab('csdd')}
            onOpenTesting={() => setDocumentSubtab('test-procedure')}
            onOpenDeliverables={() => setDocumentSubtab('asset-inventory')}
          />
        )}
        {activeTab === 'overview' && (
          <>
          {/* Word RA badge, spans full width above the Systems / Contacts grid */}
          {raDocuments.length > 0 && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 dark:text-white">Word RA</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {raDocuments[0].class_society ? `${raDocuments[0].class_society} · ` : ''}
                  {raDocuments[0].vessel_name ? `${raDocuments[0].vessel_name} · ` : ''}
                  {t('projectDetail.parsedOn', { date: new Date(raDocuments[0].parsed_at).toLocaleDateString() })}
                  {raDocuments.length > 1 ? ` · ${t('projectDetail.earlierRevisions', { count: raDocuments.length - 1 })}` : ''}
                </div>
              </div>
              <button
                onClick={() => setActiveTab('word-ra')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
              >
                {t('projectDetail.open')}
              </button>
            </div>
          )}
          {raDocuments.length === 0 && canUploadFiles && !raDocsLoading && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg">
              <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Word RA</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {t('projectDetail.noWordRa')}
                </div>
              </div>
              <button
                onClick={() => setRaImportOpen(true)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
              >
                {t('projectDetail.uploadWordRa')}
              </button>
            </div>
          )}

          {/* minmax(0,1fr): a bare 1fr track's implicit minimum is the content's
              min-content width, so one system with a paragraph-length description
              (nowrap via truncate) blows the column out to thousands of px and
              pushes the header controls + Contacts card off-screen */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-6">

        {/* Systems */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Cpu className="h-4 w-4 text-slate-400" />
                {t('nav.systems')}
                <span className="text-xs font-normal text-slate-400">
                  {(systemFilter || statusFilter !== 'all')
                    ? `${systemsByFunction.reduce((n, [, l]) => n + l.length, 0)} of ${systems.length}`
                    : systems.length}
                </span>
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative w-56">
                  <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    value={systemFilter}
                    onChange={e => setSystemFilter(e.target.value)}
                    placeholder={t('projectDetail.filterSystems')}
                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div className="inline-flex rounded bg-slate-100 dark:bg-slate-900 p-0.5">
                  {([
                    { key: 'all',         label: t('projectDetail.filters.all') },
                    { key: 'ra_missing',  label: t('projectDetail.filters.raMissing') },
                    { key: 'ra_partial',  label: t('projectDetail.filters.raPartial') },
                    { key: 'inv_missing', label: t('projectDetail.filters.inventoryMissing') },
                    { key: 'complete',    label: t('projectDetail.filters.complete') },
                  ] as const).map(p => {
                    const active = statusFilter === p.key
                    const count = p.key === 'all'
                      ? systems.length
                      : systems.filter(s => matchesPresenceFilter(s, p.key)).length
                    return (
                      <button
                        key={p.key}
                        onClick={() => setStatusFilter(p.key)}
                        className={
                          'px-2.5 py-1 text-xs rounded transition ' +
                          (active
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200')
                        }
                      >
                        {p.label}
                        {p.key !== 'all' && count > 0 && (
                          <span className="ml-1 text-[10px] text-slate-400">({count})</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
            {/* requirements checklist rollup — computed from the same per-row
                statuses the table shows, so the strip always matches the list */}
            {systems.length > 0 && (() => {
              const raDone    = systems.filter(s => s.ra_status === 'done').length
              const raPartial = systems.filter(s => s.ra_status === 'partial').length
              const invDone   = systems.filter(s => s.inventory_status === 'done').length
              const complete  = systems.filter(s => s.requirements_met).length
              return (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded bg-slate-50 dark:bg-slate-900/40 px-2.5 py-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                  <span className="font-semibold tabular-nums">
                    {t('projectDetail.systemsComplete', { complete, total: systems.length })}
                  </span>
                  <span className="tabular-nums">
                    RA data: {raDone} done{raPartial > 0 ? ` · ${raPartial} partial` : ''} · {systems.length - raDone - raPartial} missing
                  </span>
                  <span className="tabular-nums">
                    Asset inventory: {invDone} done · {systems.length - invDone} missing
                  </span>
                </div>
              )
            })()}
          </CardHeader>
          <CardContent className="p-0">
            {systems.length === 0 ? (
              <div className="py-12 text-center text-slate-500 dark:text-slate-400 px-6">
                <Cpu className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">{t('projectDetail.noSystems')}</p>
                <p className="text-sm mt-1">{t('projectDetail.importToAddSystems')}</p>
                {canEdit && (
                  <Button
                    size="sm"
                    className="mt-4"
                    onClick={() => navigate(`/projects/${id}/import`)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t('projectDetail.importWorkbook')}
                  </Button>
                )}
              </div>
            ) : systemsByFunction.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                {t('projectDetail.noSystemMatch')}
                {(systemFilter || statusFilter !== 'all') && (
                  <button
                    onClick={() => { setSystemFilter(''); setStatusFilter('all') }}
                    className="ml-2 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('assetInventory.clearFilters')}
                  </button>
                )}
              </div>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-200 dark:divide-slate-700">
                {systemsByFunction.map(([func, list]) => (
                  <div key={func}>
                    <div className={`sticky top-0 z-10 px-4 py-1.5 backdrop-blur text-[11px] font-semibold uppercase tracking-wide flex items-center justify-between ${
                      func === OUT_OF_SCOPE_GROUP
                        ? 'bg-slate-200/80 dark:bg-slate-900/70 text-slate-500 dark:text-slate-400'
                        : 'bg-slate-100 dark:bg-slate-700/70 text-slate-600 dark:text-slate-300'
                    }`}>
                      <span className="truncate">{func === OUT_OF_SCOPE_GROUP ? t('projectDetail.outOfScope') : func}</span>
                      <span className="text-slate-400 font-normal">{list.length}</span>
                    </div>
                    <ul>
                      {list.map(s => (
                        <li
                          key={s.id}
                          onClick={() => navigate(`/projects/${id}/systems/${s.id}`)}
                          onContextMenu={canEdit ? (e) => {
                            e.preventDefault()
                            setCtxMenu({ x: e.clientX, y: e.clientY, system: s })
                          } : undefined}
                          className="group px-4 py-2.5 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 border-b border-slate-100 dark:border-slate-700/60 last:border-b-0 cursor-pointer"
                        >
                          <span
                            title={s.contact_count > 0 ? `${s.contact_count} contact${s.contact_count !== 1 ? 's' : ''}` : 'No contacts'}
                            className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                              s.contact_count > 0 ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            {/* primary = the system IDENTITY. full-data recon (X) showed the
                                identity reliably lives in system_description (the register's
                                "System" column: "ECDIS", "Steering gear system", "Anemometer"),
                                while system_name is a heterogeneous secondary (model code,
                                category, or a repeat). so the bold/black primary reads
                                system_description, falling back to system_name when a row only
                                carries that */}
                            <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                              {(s.system_description ?? '').trim() || (s.system_name ?? '').trim() || '—'}
                            </div>
                            {/* secondary = system_name (model / type / supplementary), grey +
                                normal weight. shown only when it's a DISTINCT value from the
                                identity, so single-field rows (incl. backfilled rows where
                                name === description) don't render a redundant grey line. vendor
                                rides on the same subordinate line */}
                            {(() => {
                              const identity = (s.system_description ?? '').trim()
                              const model    = (s.system_name ?? '').trim()
                              const secondary = identity && model && model !== identity ? model : null
                              if (!secondary && !s.vendor_name_raw) return null
                              return (
                                <div className="text-xs font-normal text-slate-500 dark:text-slate-400 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                  {secondary && <span>{secondary}</span>}
                                  {s.vendor_name_raw && <span>{s.vendor_name_raw}</span>}
                                </div>
                              )
                            })()}
                            {(() => {
                              const names = systemContactsMap.get(s.id)
                              if (!names || names.length === 0) return null
                              const shown = names.slice(0, 2).join(', ')
                              const extra = names.length > 2 ? ` +${names.length - 2}` : ''
                              return (
                                <div
                                  className="mt-0.5 truncate text-[10px] text-blue-600 dark:text-blue-400"
                                  title={names.join(', ')}
                                >
                                  PIC: {shown}{extra}
                                </div>
                              )
                            })()}
                          </div>
                          <div className="shrink-0 flex items-center gap-1">
                            {scopeUncertainMark(s)}
                            {requirementMarks(s)}
                            {cbsGradeBadge(s.cbs_grade)}
                            {taBadge(s.type_approval_status)}
                            {canEdit && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setCtxMenu({ x: e.clientX, y: e.clientY, system: s })
                                }}
                                title={t('common.actions')}
                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-600/60"
                              >
                                ⋮
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right column */}
        <div className="space-y-6">

          {/* Contacts */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-400" />
                  {t('nav.contacts')}
                  <span className="text-xs font-normal text-slate-400">{contacts.length}</span>
                </div>
                <button
                  onClick={() => navigate(`/contacts?project=${id}`)}
                  className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline font-normal"
                >
                  {t('dashboard.recentPentests.viewAll')}
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {contacts.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  {t('projectDetail.noContacts')}
                </div>
              ) : (
                <ul className="max-h-[28rem] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/60">
                  {contacts.map(c => (
                    <li key={c.id} className="px-4 py-3 flex items-start gap-3">
                      {(() => {
                        const { initials, showFull } = contactDisplay(c.full_name)
                        return (
                          <div
                            className={`rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center font-semibold shrink-0 ${
                              showFull
                                ? 'h-8 min-w-[32px] w-auto px-2 text-[11px]'
                                : 'h-8 w-8 text-xs'
                            }`}
                          >
                            {initials}
                          </div>
                        )
                      })()}
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="font-medium text-sm truncate text-slate-900 dark:text-white">
                          {c.full_name ?? '—'}
                        </div>
                        {c.company_name && (
                          <div className="text-slate-500 dark:text-slate-400 truncate">{c.company_name}</div>
                        )}
                        {/* the yard's own person for this contact. Only an
                            SHI form carries it, so it is silent everywhere
                            else rather than an empty labelled row. */}
                        {c.shipyard_contacts && c.shipyard_contacts.length > 0 && (
                          <div className="mt-0.5 truncate text-slate-500 dark:text-slate-400">
                            <span className="text-slate-400">Yard PIC:</span>{' '}
                            <span className="font-medium text-slate-600 dark:text-slate-300">
                              {c.shipyard_contacts.join(', ')}
                            </span>
                            {c.shipyard_contact_emails && c.shipyard_contact_emails.length > 0 && (
                              <span className="ml-1 text-slate-400">
                                ({c.shipyard_contact_emails.join(', ')})
                              </span>
                            )}
                          </div>
                        )}
                        {c.email && (
                          <a
                            href={`mailto:${c.email}`}
                            className="mt-1 flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline truncate"
                          >
                            <Mail className="h-3 w-3 shrink-0" />
                            <span className="truncate">{c.email}</span>
                          </a>
                        )}
                        {c.phone && (
                          <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 truncate">
                            <Phone className="h-3 w-3 shrink-0" />
                            {c.phone}
                          </div>
                        )}
                        {c.linked_systems_count > 0 && (
                          <div
                            className="mt-1 text-[10px] text-slate-400 truncate"
                            title={c.linked_system_names.join(', ')}
                          >
                            {c.linked_systems_count} system{c.linked_systems_count !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* TA summary */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('fields.typeApproval')}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-2">
              <TaBox label={t('projectDetail.approved')} value={taSummary.approved} tone="green" />
              <TaBox label={t('projectDetail.pending')} value={taSummary.pending} tone="amber" />
              <TaBox label={t('projectDetail.other')} value={taSummary.other} tone="slate" />
            </CardContent>
          </Card>

          {/* Import history */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">{t('projectDetail.importHistory')}</CardTitle>
              {canUploadFiles && (
                <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${id}/import`)}>
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {t('projectDetail.new')}
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {project.batches.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  <Upload className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  {t('projectDetail.noImports')}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-700/60 max-h-72 overflow-y-auto">
                  {project.batches.map(b => (
                    <li key={b.id} className="px-4 py-2.5 flex items-center gap-2 text-xs">
                      {batchStatusDot(b)}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono truncate text-slate-700 dark:text-slate-200">
                          {b.original_filename ?? '(no name)'}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {b.row_count} row{b.row_count !== 1 ? 's' : ''} · {new Date(b.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      {canEdit && b.status === 'draft' && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Link to={`/projects/${id}/import?batch=${b.id}`}>
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                              {t('projectDetail.review')}
                            </Button>
                          </Link>
                          <button
                            onClick={() => handleDeleteBatch(b.id)}
                            disabled={deleting === b.id}
                            title={t('projectDetail.deleteDraft')}
                            className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

        </div>
          </div>
          </>
        )}
        {activeTab === 'word-ra' && id && (
          <WordRaProjectTab
            projectId={id}
            canEdit={canEdit}
            complianceProfiles={project.compliance_profiles ?? []}
            raDocuments={raDocuments}
            onRefreshDocs={loadRaDocuments}
          />
        )}
        {activeTab === 'nms' && id && (
          <NmsProjectTab projectId={id} />
        )}
        {activeTab === 'wiring' && id && canEdit && (
          // a throw in here used to take the whole page; keep it to the tab
          <ErrorBoundary where="wiring tab">
          <ScreeningTab
            projectId={id}
            droppedFiles={wiringDropFiles}
            onDroppedConsumed={() => setWiringDropFiles(null)}
            onBackToCompliance={() => setActiveTab('compliance')}
          />
          </ErrorBoundary>
        )}
        {activeTab === 'documents' && id && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setActiveTab('compliance')}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('projectDetail.backToCompliance')}
              </button>
              <span className="text-xs text-slate-400">{t('projectDetail.documentGeneration')}</span>
            </div>
            <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800">
              {([
                { key: 'risk-assessment', label: t('projectDetail.riskAssessment') },
                { key: 'asset-inventory', label: `${t('nav.assetInventory')}${inventoryCount ? ` (${inventoryCount})` : ''}` },
                { key: 'zones', label: t('projectDetail.tabs.zones') },
                { key: 'csdd', label: 'CSDD' },
                { key: 'test-procedure', label: 'Test Procedure' },
              ] as const).map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setDocumentSubtab(tab.key)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    activeDocumentSubtab === tab.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {activeDocumentSubtab === 'zones' ? (
              <ErrorBoundary where="zone and conduit workspace">
                <ZoneConduitWorkspace
                  complianceProfiles={project.compliance_profiles ?? []}
                  projectId={id}
                  canEdit={canEdit}
                  onBack={() => setActiveTab('compliance')}
                  onOpenCsdd={() => setDocumentSubtab('csdd')}
                />
              </ErrorBoundary>
            ) : activeDocumentSubtab === 'csdd' ? (
              <ErrorBoundary where="CSDD workspace">
                <CsddWorkspace
                  complianceProfiles={project.compliance_profiles ?? []}
                  projectId={id}
                  canEdit={canEdit}
                  onBack={() => setActiveTab('compliance')}
                  onOpenZones={() => setDocumentSubtab('zones')}
                />
              </ErrorBoundary>
            ) : activeDocumentSubtab === 'test-procedure' ? (
              <ErrorBoundary where="Test Procedure workspace">
                <TestProcedureWorkspace
                  projectId={id}
                  projectHull={project.hull_number}
                  initialProfileCode={
                    project.compliance_profiles?.find(profile => profile.class_society === 'ABS')?.profile_code ??
                    project.compliance_profiles?.[0]?.profile_code ??
                    null
                  }
                  canEdit={canEdit}
                  canGenerate={canGenerateFiles}
                />
              </ErrorBoundary>
            ) : activeDocumentSubtab === 'risk-assessment' ? (
              <RaGenerationWorkspace
                projectId={id}
                projectHull={project.hull_number}
                initialProfileCode={project.compliance_profiles?.find(profile => profile.class_society === 'LR')?.profile_code ?? project.compliance_profiles?.[0]?.profile_code ?? null}
                canEdit={canEdit}
              />
            ) : (
              <>
            <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase text-slate-400">{t('projectDetail.deliverables')}</p>
                <button
                  type="button"
                  onClick={loadDeliverables}
                  disabled={deliverablesLoading}
                  title={t('common.refresh')}
                  className="inline-flex h-6 shrink-0 items-center rounded-md border border-slate-200 px-1.5 text-xs hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-700"
                >
                  <RefreshCw className={`h-3 w-3 ${deliverablesLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {t('projectDetail.deliverablesDescription')}
              </p>
            </div>

            {/* project requirements checklist — server-computed with the same
                predicates as the dashboard, so the numbers always agree */}
            {requirements && requirements.total > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/50">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[10px] uppercase text-slate-400">{t('projectDetail.requirements')}</p>
                  <p className="text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                    {t('projectDetail.systemsComplete', { complete: requirements.complete, total: requirements.total })}
                  </p>
                </div>
                <div className="mt-1.5 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                  <p className="tabular-nums">
                    RA data — {requirements.ra.done} done
                    {requirements.ra.partial > 0 && <>, {requirements.ra.partial} partial</>}
                    , {requirements.ra.missing} missing
                    <span className="ml-1 text-[10px] text-slate-400">{t('projectDetail.raCountsHint')}</span>
                  </p>
                  <p className="tabular-nums">
                    {t('projectDetail.inventoryRequirement', { done: requirements.inventory.done, missing: requirements.inventory.missing })}
                    <span className="ml-1 text-[10px] text-slate-400">{t('projectDetail.inventoryCountsHint')}</span>
                  </p>
                </div>
                {requirements.complete < requirements.total && (
                  <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto border-t border-slate-200/80 pt-1.5 text-[11px] dark:border-slate-700/60">
                    {requirements.systems.filter(s => !s.complete).map(s => (
                      <li key={s.id} className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-slate-600 dark:text-slate-300">{s.name ?? 'Unnamed system'}</span>
                        <span className="shrink-0 tabular-nums text-slate-400">
                          RA {s.ra_status} · inventory {s.inventory_status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {deliverables === null ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {[0, 1].map(i => (
                  <div key={i} className="h-28 animate-pulse rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/50" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {deliverables.filter(d => d.key !== 'ra').map(d => (
                  <DeliverableCard
                    key={d.key}
                    d={d}
                    canDownload={canDownloadFiles}
                    downloading={downloadingKey === d.key}
                    onDownload={() => handleDeliverableDownload(d)}
                  />
                ))}
              </div>
            )}
            <DirtyFormProvider context="asset_inventory" onSaved={() => loadProject()}>
              <DirtyChangesBar />
              <AssetInventoryTab
                rows={inventoryRows}
                canEdit={canEditInventory}
                onChanged={loadProject}
                onRowUpdated={(updated) => {
                  setInventoryRows(prev => prev.map(row => row.id === updated.id ? updated : row))
                }}
              />
            </DirtyFormProvider>
              </>
            )}
          </div>
        )}
      </div>

      {canUploadFiles && inventoryModalOpen && id && (
        <ImportAssetInventoryModal
          projectId={id}
          onClose={() => setInventoryModalOpen(false)}
          onSuccess={() => {
            setInventoryModalOpen(false)
            loadProject()
          }}
        />
      )}

      {canUploadFiles && raImportOpen && id && (
        <ImportRaWordModal
          projectId={id}
          complianceProfiles={project?.compliance_profiles ?? []}
          onClose={() => setRaImportOpen(false)}
          onCommitted={() => {
            setRaImportOpen(false)
            loadRaDocuments()
            loadProject()
          }}
        />
      )}

    </div>
  )
}

const StatItem: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-slate-400 uppercase tracking-wide text-[10px]">{label}</span>
    <span className={`text-slate-700 dark:text-slate-200 ${mono ? 'font-mono' : 'font-medium'}`}>
      {value}
    </span>
  </div>
)

const TaBox: React.FC<{ label: string; value: number; tone: 'green' | 'amber' | 'slate' }> = ({ label, value, tone }) => {
  const cls = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    slate: 'bg-slate-50 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600',
  }[tone]
  return (
    <div className={`rounded-lg border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  )
}

// one deliverable row for the Documents tab. presence-gated: "Available" +
// download when the data is present/complete, "Missing" + reason otherwise.
// asset inventory shows state only until its formatted generator lands.
function DeliverableCard({ d, canDownload: mayDownload, downloading, onDownload }: {
  d: ProjectDeliverable
  canDownload: boolean
  downloading: boolean
  onDownload: () => void
}) {
  const { t } = useTranslation()
  const Icon = d.key === 'asset_inventory' ? Package : FileText
  const isRa = d.key === 'ra'
  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-white">{d.label}</div>
            {d.detail && <div className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400">{d.detail}</div>}
          </div>
        </div>
        {d.available ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <CheckCircle className="h-3 w-3" /> {t('projectDetail.available')}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-700 dark:text-slate-300">
            {t('projectDetail.missing')}
          </span>
        )}
      </div>

      {!d.available && d.reason && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{d.reason}</span>
        </p>
      )}

      {/* soft-gate warning: Available but with incomplete rows (asset
          inventory). non-blocking — download stays enabled. */}
      {d.available && d.warning && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{d.warning}</span>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!mayDownload ? (
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Download not permitted
          </Button>
        ) : isRa ? (
          <>
            {d.available && mayDownload && (
              <Button size="sm" className="h-7 px-2 text-xs" onClick={onDownload} disabled={downloading}>
                {downloading
                  ? <><RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {t('projectDetail.generating')}</>
                  : <><Download className="mr-1.5 h-3.5 w-3.5" /> {t('common.download')}</>}
              </Button>
            )}
          </>
        ) : d.available && d.generator_ready ? (
          <Button size="sm" className="h-7 px-2 text-xs" onClick={onDownload} disabled={downloading}>
            {downloading
              ? <><RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {t('projectDetail.generating')}</>
              : <><Download className="mr-1.5 h-3.5 w-3.5" /> {t('common.download')}</>}
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled
                  title={d.available ? t('projectDetail.generatorComingTitle') : t('projectDetail.noDataYet')}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {d.available ? t('projectDetail.generatorComing') : t('projectDetail.unavailable')}
          </Button>
        )}
      </div>
    </div>
  )
}
